/**
 * Generated-section renderers — the page content rendered close to how the live UI shows
 * it (Jaume, 2026-08-28: "formatted in a similar way to what we are doing in the UI").
 *
 * Everything printed here is VERBATIM `aeo_content` from the database; cockpit chrome is
 * limited to muted labels. Two rules make this trustworthy:
 *  - an unknown section shape falls back to a generic JSON walker rather than showing
 *    nothing — a section the renderer does not recognise still shows its full content;
 *  - a renderer that throws falls back to the same walker AND says it failed, because a
 *    renderer must never silently hide content.
 */
import React from 'react';

type Json = unknown;

// ── atoms ──────────────────────────────────────────────────────────────────
const Paras = ({ text }: { text: unknown }) => (
  <>
    {String(text ?? '').split(/\n+/).filter(Boolean).map((x, i) => (
      <p className="pmc-gp" key={i}>{x}</p>
    ))}
  </>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="pmc-glabel">{children}</div>
);

const Pills = ({ items }: { items?: unknown[] | null }) => (
  items?.length
    ? (
      <div className="pmc-gpills">
        {items.map((x, i) => <span className="pmc-gpill" key={i}>{String(x)}</span>)}
      </div>
    )
    : null
);

const Mono = ({ children }: { children: React.ReactNode }) => (
  <span className="pmc-mono pmc-small pmc-mute">{children}</span>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="pmc-gcard">{children}</div>
);

const CardTitle = ({ title, extra }: { title: React.ReactNode; extra?: React.ReactNode }) => (
  <div className="pmc-gcard-hd"><b>{title}</b>{extra ?? null}</div>
);

const asArray = (v: Json): any[] => (Array.isArray(v) ? v : []);

// ── generic fallback: renders ANY JSON honestly, depth-capped ───────────────
export function RenderGeneric({ value, depth = 0 }: { value: Json; depth?: number }) {
  if (value === null || value === undefined) return <span className="pmc-mute">—</span>;
  if (typeof value === 'string') {
    return depth ? <span>{value}</span> : <div><Paras text={value} /></div>;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="pmc-mono">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="pmc-mute">(empty list)</span>;
    if (value.every((x) => typeof x === 'string' || typeof x === 'number')) {
      return <Pills items={value} />;
    }
    return (
      <div>
        {value.slice(0, 30).map((item, i) => (
          <Card key={i}><RenderGeneric value={item} depth={depth + 1} /></Card>
        ))}
        {value.length > 30 && (
          <div className="pmc-small pmc-mute">… {value.length - 30} more items</div>
        )}
      </div>
    );
  }
  if (depth >= 3) {
    return <Mono>{JSON.stringify(value).slice(0, 120)}</Mono>;
  }
  const obj = value as Record<string, Json>;
  return (
    <div className={depth ? undefined : 'pmc-ggen'}>
      {Object.keys(obj).map((k) => {
        const val = obj[k];
        if (val === null || val === undefined) return null;
        if (typeof val === 'object' && depth >= 1) {
          return (
            <details className="pmc-gdet" key={k}>
              <summary>{k.replace(/_/g, ' ')}</summary>
              <RenderGeneric value={val} depth={depth + 1} />
            </details>
          );
        }
        return (
          <React.Fragment key={k}>
            <Label>{k.replace(/_/g, ' ')}</Label>
            <RenderGeneric value={val} depth={depth + 1} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * What each family renderer actually requires. A renderer is only chosen when its shape
 * holds; otherwise the generic walker shows the content rather than nothing.
 */
const isObj = (v: Json): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
/** does the object carry at least one field this renderer knows how to show? */
const has = (v: Json, ...keys: string[]) => isObj(v) && keys.some((k) => v[k] !== undefined && v[k] !== null);
/**
 * An array guard must ask the same question the object guards ask: can the renderer SHOW
 * anything from this?
 *
 * ⛔ `Array.isArray` alone let `["Recorded legacy answer"]` and `[{text: "…"}]` through —
 * both render an empty FAQ shell, throw nothing, and so never reach the generic walker that
 * would have shown the text. Recorded content vanished with no error and no fallback, which
 * is the one outcome this whole guard layer exists to prevent.
 *
 * An EMPTY array passes: there is nothing to lose, and "no items" is a legitimate section.
 */
/**
 * The fields each array family's renderer can actually SHOW, in one place so the shape guard
 * and the per-item split below cannot drift apart.
 */
const ARRAY_KEYS: Record<string, string[]> = {
  faq: ['question', 'answer', 'category', 'source_attributes'],
  best_for: ['buyer_type', 'reason', 'evidence', 'badge_text'],
  use_cases: ['use_case', 'why_suitable', 'key_features', 'limitations', 'fit_score', 'fit_rating', 'grounding'],
  specifications: ['display_name', 'attribute', 'value', 'unit'],
  benefits: ['benefit_name', 'headline', 'explanation', 'confidence'],
  recommendations: ['product_name', 'scenario', 'reason', 'type'],
};
const showable = (name: string, item: unknown): boolean => {
  const keys = ARRAY_KEYS[name];
  return !!keys && has(item as Json, ...keys);
};
/**
 * The family is plausible when ANY item fits it — but "plausible" is not "renders whole",
 * so the dispatch below splits the array and shows the rest generically. Requiring every
 * item here instead would have sent a 20-item FAQ with one legacy entry entirely to the
 * generic walker: nothing lost, but everything degraded by one stray row.
 */
const arrayOf = (name: string) => (v: Json): boolean => (
  Array.isArray(v) && (v.length === 0 || v.some((item) => showable(name, item)))
);
const SHAPE_OK: Record<string, (v: Json) => boolean> = {
  // "is an object" is not enough: the renderer reads specific fields, so `{text: "…"}`
  // passed the guard and then rendered nothing at all, with no error to trip the boundary.
  // A guard must require content the renderer can actually SHOW.
  summary: (v) => has(v, 'featured_snippet', 'full_description', 'key_differentiators', 'semantic_anchors'),
  faq: arrayOf('faq'),
  best_for: arrayOf('best_for'),
  use_cases: arrayOf('use_cases'),
  buyer_profiles: (v) => isObj(v) && has(v, 'profiles', 'not_ideal_for')
    && (v.profiles === undefined || Array.isArray(v.profiles)),
  specifications: arrayOf('specifications'),
  benefits: arrayOf('benefits'),
  limitations: (v) => isObj(v) && has(v, 'limitations', 'items', 'overall_assessment', 'assessment')
    && (v.limitations === undefined || Array.isArray(v.limitations))
    && (v.items === undefined || Array.isArray(v.items)),
  recommendations: arrayOf('recommendations'),
  problem_solver: (v) => isObj(v) && has(v, 'problems', 'headline')
    && (v.problems === undefined || Array.isArray(v.problems)),
  comparison: (v) => has(v, 'comparison_intro', 'where_it_excels', 'where_to_consider_alternatives',
    'ideal_buyer', 'overall_verdict'),
  smart_alternatives: (v) => isObj(v) && has(v, 'products', 'method')
    && (v.products === undefined || Array.isArray(v.products)),
  comparison_products: (v) => has(v, 'brand_tiers', 'tiers', 'tier_explanations',
    'attribute_comparison', 'competitor_alternatives', 'retrieval_telemetry'),
};

// ── per-family renderers ───────────────────────────────────────────────────
const R: Record<string, (v: any) => React.ReactElement> = {
  summary: (v) => (
    <div>
      {v.featured_snippet && (
        <div className="pmc-gsnippet">
          <Label>featured snippet</Label>
          <Paras text={v.featured_snippet} />
        </div>
      )}
      <Paras text={v.full_description} />
      {!!v.key_differentiators?.length && (
        <div><Label>key differentiators</Label><Pills items={v.key_differentiators} /></div>
      )}
      {!!v.semantic_anchors?.length && (
        <div><Label>semantic anchors</Label><Pills items={v.semantic_anchors} /></div>
      )}
    </div>
  ),

  faq: (v) => (
    <div>
      {asArray(v).map((q, i) => (
        <div className="pmc-gqa" key={i}>
          <div className="pmc-gq">
            {'Q — '}{q.question}
            {q.category && <span className="pmc-gpill pmc-gq-cat">{q.category}</span>}
          </div>
          <div className="pmc-ga">{q.answer}</div>
          {!!q.source_attributes?.length && <Mono>from: {q.source_attributes.join(', ')}</Mono>}
        </div>
      ))}
    </div>
  ),

  best_for: (v) => (
    <div>
      {asArray(v).map((b, i) => (
        <Card key={i}>
          <CardTitle title={b.buyer_type ?? '—'} extra={b.badge_text ? <span className="pmc-gbadge">{b.badge_text}</span> : null} />
          <Paras text={b.reason} />
          {b.evidence && <Mono>{b.evidence}</Mono>}
        </Card>
      ))}
    </div>
  ),

  use_cases: (v) => (
    <div>
      {asArray(v).map((u, i) => (
        <Card key={i}>
          <CardTitle
            title={u.use_case ?? '—'}
            extra={u.fit_score != null
              ? <span className="pmc-gbadge">{`fit ${u.fit_score}${u.fit_rating ? ` · ${u.fit_rating}` : ''}`}</span>
              : null}
          />
          <Paras text={u.why_suitable} />
          <Pills items={u.key_features} />
          {/*
            The generator emits `limitations` for every use case
            (AEOProductPageService.js:24830 @ e3f69fa8f) — the restriction that QUALIFIES the
            suitability just rendered above it. Dropping it showed the reader the fit and hid
            the caveat: "Not compatible with mountain bikes" disappeared entirely while its
            use case still read as a good fit. `null` means the generator found none.
          */}
          {typeof u.limitations === 'string' && u.limitations.trim() && (
            <div className="pmc-uc-limit">
              <Label>limitations</Label>
              <Paras text={u.limitations} />
            </div>
          )}
          {u.grounding && typeof u.grounding === 'object' && (
            <Mono>
              {Object.entries(u.grounding).map(([k, x]) => `${k}: ${x}`).join(' · ').slice(0, 200)}
            </Mono>
          )}
        </Card>
      ))}
    </div>
  ),

  buyer_profiles: (v) => (
    <div>
      {(v.profiles ?? []).map((pr: any, i: number) => (
        <Card key={i}>
          <CardTitle title={pr.profile_name ?? '—'} />
          <Paras text={pr.description} />
          <Pills items={pr.key_priorities} />
          {pr.why_this_product && <div className="pmc-small">{pr.why_this_product}</div>}
        </Card>
      ))}
      {!!(v.not_ideal_for ?? []).length && (
        <div>
          <Label>not ideal for</Label>
          {v.not_ideal_for.map((n: any, i: number) => (
            <Card key={i}>
              <CardTitle title={n.profile ?? '—'} />
              <Paras text={n.reason} />
              {n.better_alternative && (
                <div className="pmc-small pmc-mute">better alternative: {n.better_alternative}</div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  ),

  specifications: (v) => (
    <div className="pmc-gtablewrap">
      <table className="pmc-gtable">
        <thead>
          <tr><th>specification</th><th>value</th><th>attribute</th></tr>
        </thead>
        <tbody>
          {asArray(v).map((s, i) => (
            <tr key={i}>
              <td>{s.display_name ?? s.attribute ?? '—'}</td>
              <td>{String(s.value ?? '—') + (s.unit ? ` ${s.unit}` : '')}</td>
              <td><Mono>{`${s.attribute ?? ''}${s.confidence != null ? ` · conf ${s.confidence}` : ''}`}</Mono></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),

  benefits: (v) => (
    <div>
      {asArray(v).map((b, i) => (
        <Card key={i}>
          <CardTitle
            title={b.headline ?? b.benefit_name ?? '—'}
            extra={b.confidence != null ? <span className="pmc-gbadge">{`conf ${b.confidence}`}</span> : null}
          />
          <Paras text={b.explanation} />
          <Pills items={b.evidence} />
          <Mono>{[b.benefit_name, b.benefit_layer].filter(Boolean).join(' · ')}</Mono>
        </Card>
      ))}
    </div>
  ),

  limitations: (v) => (
    <div>
      {(v.limitations ?? v.items ?? []).map((l: any, i: number) => (
        <Card key={i}>
          <CardTitle title={l.limitation ?? '—'} />
          {l.impact && <div className="pmc-small"><b className="pmc-mute">impact · </b>{l.impact}</div>}
          {l.mitigation && <div className="pmc-small"><b className="pmc-mute">mitigation · </b>{l.mitigation}</div>}
          {l.who_affected && <div className="pmc-small pmc-mute">who: {l.who_affected}</div>}
        </Card>
      ))}
      {/* the synchronous generator persists `assessment` (AEOProductPageService.js:25044),
          the batch path persists `overall_assessment` — reading one dropped the other */}
      {(v.overall_assessment || v.assessment) && (
        <div><Label>overall assessment</Label><Paras text={v.overall_assessment || v.assessment} /></div>
      )}
    </div>
  ),

  recommendations: (v) => (
    <div>
      {asArray(v).map((r, i) => (
        <Card key={i}>
          <CardTitle
            title={r.product_name ?? r.scenario ?? '—'}
            extra={r.type ? <span className="pmc-gpill">{r.type}</span> : null}
          />
          {r.scenario && r.product_name && <div className="pmc-small pmc-mute">{r.scenario}</div>}
          <Paras text={r.reason} />
        </Card>
      ))}
    </div>
  ),

  problem_solver: (v) => (
    <div>
      {v.headline && <div className="pmc-gsnippet"><Paras text={v.headline} /></div>}
      {(v.problems ?? []).map((pr: any, i: number) => (
        <Card key={i}>
          <CardTitle title={pr.problem ?? '—'} extra={pr.key_feature ? <span className="pmc-gpill">{pr.key_feature}</span> : null} />
          <Paras text={pr.how_it_solves} />
        </Card>
      ))}
    </div>
  ),

  comparison: (v) => (
    <div>
      <Paras text={v.comparison_intro} />
      {!!v.where_it_excels?.length && (
        <div>
          <Label>where it excels</Label>
          <ul className="pmc-glist">{v.where_it_excels.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul>
        </div>
      )}
      {!!v.where_to_consider_alternatives?.length && (
        <div>
          <Label>where to consider alternatives</Label>
          <ul className="pmc-glist">{v.where_to_consider_alternatives.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul>
        </div>
      )}
      {v.ideal_buyer && <div><Label>ideal buyer</Label><Paras text={v.ideal_buyer} /></div>}
      {v.overall_verdict && <div><Label>overall verdict</Label><Paras text={v.overall_verdict} /></div>}
    </div>
  ),

  smart_alternatives: (v) => (
    <div>
      {(v.products ?? []).map((a: any, i: number) => (
        <Card key={i}>
          <CardTitle
            title={a.name ?? '—'}
            extra={a.totalScore != null ? <span className="pmc-gbadge">{`score ${a.totalScore}`}</span> : null}
          />
          <div className="pmc-small pmc-mute">{[a.brand, a.price].filter(Boolean).join(' · ')}</div>
          <Pills items={a.matchedBenefits} />
        </Card>
      ))}
      {v.method && <Mono>method: {v.method}</Mono>}
    </div>
  ),

  comparison_products: (v) => {
    const bt = v.brand_tiers ?? {};
    return (
      <div>
        {(bt.brand || bt.brand_market_tier || bt.current_product_tier) && (
          <div>
            <Label>brand tiering</Label>
            <div className="pmc-gkv">
              <span className="pmc-k">brand</span><span>{bt.brand ?? '—'}</span>
              <span className="pmc-k">brand market tier</span><span>{bt.brand_market_tier ?? '—'}</span>
              <span className="pmc-k">this product’s tier</span><span>{bt.current_product_tier ?? '—'}</span>
              {bt.stats && (
                <>
                  <span className="pmc-k">products considered</span>
                  <span>{String(bt.stats.products_considered ?? '—')}</span>
                  <span className="pmc-k">avg similarity</span>
                  <span>{bt.stats.avg_similarity != null ? Number(bt.stats.avg_similarity).toFixed(3) : '—'}</span>
                </>
              )}
            </div>
          </div>
        )}
        {['tiers', 'tier_explanations', 'attribute_comparison', 'competitor_alternatives', 'retrieval_telemetry']
          .filter((k) => v[k] ?? bt[k])
          .map((k) => (
            <details className="pmc-gdet" key={k}>
              <summary>{k.replace(/_/g, ' ')}</summary>
              <RenderGeneric value={v[k] ?? bt[k]} depth={1} />
            </details>
          ))}
      </div>
    );
  },
};

/** Runs one family renderer as a child, so a throw happens BELOW the boundary. */
function RenderFamily({ fn, value }: { fn: (v: any) => React.ReactElement; value: Json }) {
  return fn(value);
}

/** A renderer that throws must not hide the content: fall back and say so. */
class RendererBoundary extends React.Component<
  { name: string; fallback: React.ReactNode; children: React.ReactNode },
  { error: string | null }
> {
  constructor(props: { name: string; fallback: React.ReactNode; children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error: error.message };
  }

  render() {
    if (this.state.error !== null) {
      return (
        <div>
          <div className="pmc-small pmc-syn">
            ⚠ {this.props.name} renderer failed ({this.state.error}) — generic rendering below
          </div>
          {this.props.fallback}
        </div>
      );
    }
    return this.props.children;
  }
}

interface SectionContentProps {
  name: string;
  contentJson: Json;
  flatText?: string | null;
  /**
   * What the TRANSPORT withheld, from sectionJsonCapped. It is not decoration: when the
   * first item alone exceeds the 30,000-char cap the contract carries an EMPTY array with
   * `kept: 0`, and an empty array read on its own says the opposite of what happened.
   */
  truncated?: { kept?: number; total?: number; full_chars?: number; note?: string } | null;
}

export function SectionContent({ name, contentJson, flatText, truncated }: SectionContentProps) {
  if (contentJson !== null && contentJson !== undefined) {
    const empty = (Array.isArray(contentJson) && !contentJson.length)
      || (typeof contentJson === 'object' && !Array.isArray(contentJson)
        && !Object.keys(contentJson as object).length);
    if (empty) {
      // ⛔ AN EMPTY ARRAY IS NOT ALWAYS AN EMPTY SECTION. sectionJsonCapped keeps items only
      // while they fit in 30,000 chars, so ONE oversized first item yields `[]` with
      // `{kept: 0, total: N}` — and this branch then asserted "the generator produced no
      // items here" about a section holding N of them. Reproduced with a FAQ whose first
      // answer is 40,000 chars: content exists, the page said it did not. The separate
      // truncation notice below does not repair the claim; it sits beside it.
      const withheld = (truncated?.total ?? 0) > 0 && !(truncated?.kept ?? 0);
      if (withheld) {
        return (
          <div className="pmc-small pmc-mute">
            {`${truncated?.total} item${truncated?.total === 1 ? '' : 's'} exist here and NONE could be `}
            {'carried to this page: the first alone is larger than the 30,000-char transport cap'}
            {truncated?.full_chars ? ` (the section is ${truncated.full_chars} chars)` : ''}
            {'. This is a limit of this view, NOT an empty section — read it in aeo_content.'}
          </div>
        );
      }
      // a genuinely empty section is a FINDING, not a rendering gap — say it plainly
      return (
        <div className="pmc-small pmc-mute">
          empty section — the generator produced no items here (recorded as such in aeo_content)
        </div>
      );
    }
    // A family renderer assumes a shape. Handing it the wrong one does not always throw —
    // `faq` given an object, or `summary` given a string, produced an EMPTY div: no error,
    // so the boundary never fired and the promised generic fallback never appeared. Content
    // vanished silently, which is the one thing this file exists to prevent. So the shape is
    // checked first, and anything unexpected takes the generic walker.
    const Fn = SHAPE_OK[name]?.(contentJson) ? R[name] : undefined;
    if (!Fn) return <RenderGeneric value={contentJson} />;
    // ⛔ A GUARD THAT ACCEPTS THE SECTION DOES NOT PROMISE EVERY ITEM RENDERS. A FAQ holding
    // one normal question/answer and one `{text: "Recorded legacy safety warning"}` passed
    // the guard on the strength of the first, and the renderer read .question/.answer off
    // the second and drew nothing — no throw, so the boundary never fired and the warning
    // was gone. So the array is SPLIT: the items this family can show keep their proper
    // rendering, and the rest are shown generically, labelled, right below them. Nothing is
    // dropped and nothing is degraded that did not have to be.
    if (Array.isArray(contentJson) && ARRAY_KEYS[name]) {
      const rest = contentJson.filter((item) => !showable(name, item));
      if (rest.length) {
        const shown = contentJson.filter((item) => showable(name, item));
        return (
          <>
            {shown.length > 0 && (
              <RendererBoundary name={name} fallback={<RenderGeneric value={shown as Json} />}>
                <RenderFamily fn={Fn} value={shown as Json} />
              </RendererBoundary>
            )}
            <div className="pmc-small pmc-mute" style={{ marginTop: 8 }}>
              {`${rest.length} recorded item${rest.length === 1 ? '' : 's'} in this section do not fit the ${name.replace(/_/g, ' ')} shape — shown as recorded, not dropped:`}
            </div>
            <RenderGeneric value={rest as Json} />
          </>
        );
      }
    }
    // Fn MUST run inside a child component, not as `{Fn(contentJson)}` here: that form is
    // evaluated while the PARENT renders, before the boundary mounts, so React never sees
    // the throw and the promised fallback never appeared. The safety this file advertises
    // only exists in this shape.
    return (
      <RendererBoundary name={name} fallback={<RenderGeneric value={contentJson} />}>
        <RenderFamily fn={Fn} value={contentJson} />
      </RendererBoundary>
    );
  }
  if (flatText) {
    return (
      <div>
        <div className="pmc-small pmc-mute">flat text (structured content not backfilled)</div>
        <Paras text={flatText} />
      </div>
    );
  }
  return <span className="pmc-mute">(empty)</span>;
}
